import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getRouteApi } from '@tanstack/react-router';
import { SlidersHorizontalIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import type { ToolbarProps } from './toolbar';

interface FilterButtonProps {
  url: ToolbarProps['url'];
}

const countActiveFilters = (value: unknown): number => {
  if (value == null) return 0;

  if (Array.isArray(value)) {
    return value.reduce<number>((count, item) => count + countActiveFilters(item), 0);
  }

  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }

  if (typeof value === 'number') {
    return Number.isNaN(value) ? 0 : 1;
  }

  if (typeof value === 'string') {
    return value.trim().length ? 1 : 0;
  }

  if (typeof value === 'object') {
    return Object.values(value).reduce<number>(
      (count, item) => count + countActiveFilters(item),
      0
    );
  }

  return 0;
};

export const FilterButton = ({ url }: FilterButtonProps) => {
  const route = getRouteApi(url);
  const { t } = useTranslation();
  const navigate = route.useNavigate();
  const search = route.useSearch();
  const { activeFiltersCount, enableFilters } = useMemo(
    () => ({
      activeFiltersCount: countActiveFilters('filters' in search ? search.filters : undefined),
      enableFilters: 'enableFilters' in search ? search.enableFilters : undefined,
    }),
    [search]
  );
  const hasActiveFilters = activeFiltersCount > 0;

  const switchEnableFilters = () => {
    navigate({
      search: (prev) => ({
        ...prev,
        enableFilters: 'enableFilters' in prev ? !prev.enableFilters : true,
      }),
      replace: true,
    });
  };

  return (
    <Button
      size="md"
      variant="outline"
      className={cn(
        'flex h-[46px] items-center justify-center gap-[6px] rounded-[6px] border border-[#F1F5F9] bg-white px-6 font-medium text-[#2F3A4A] hover:border-[#07B4C0] hover:bg-[#D9F3F7]',
        (enableFilters || hasActiveFilters) && 'border-[#BFD4FF] bg-[#F6F9FC]'
      )}
      onClick={switchEnableFilters}
    >
      <SlidersHorizontalIcon
        className={cn(
          'flex items-center transition-transform motion-reduce:transition-none',
          !enableFilters && 'rotate-180'
        )}
      />
      <span className="block max-xl:hidden">{t('filter')}</span>
      {hasActiveFilters ? (
        <span className="ml-2 flex size-8 items-center justify-center rounded-full bg-[#EEF4FF] text-sm font-semibold text-[#2F3A4A]">
          {activeFiltersCount}
        </span>
      ) : null}
    </Button>
  );
};
